import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { correspondenceAPI } from '../../services/authService';
import { notifyError, notifySuccess } from '../../utils/notify';
import {
  IconStar, IconPin, IconBookmark, IconLayout, IconSave, IconFilter,
  IconClock, IconAlert, IconCheckCircle, IconFolder,
} from './CorrespondenceViewIcons';
import './correspondenceViews.css';

const VISIBILITY_OPTIONS = [
  { value: 'PRIVATE', label: 'Private — only you' },
  { value: 'PROJECT', label: 'Project — team on selected project' },
  { value: 'COMPANY', label: 'Company — all permitted users' },
];

const visibilityBadgeClass = (v) => {
  if (v === 'PROJECT') return 'bv-badge--project';
  if (v === 'COMPANY') return 'bv-badge--company';
  return 'bv-badge--private';
};

const CorrespondenceViewsToolbar = ({
  filter,
  projects = [],
  quickFilter,
  onQuickFilterChange,
  savedViewId,
  onSavedViewChange,
  onApplySavedView,
  getCurrentViewSnapshot,
  onNavigateRegisterFilter,
  canManagePersonalFlags = true,
  canManageSavedViews = true,
}) => {
  const [savedViews, setSavedViews] = useState([]);
  const [loadingViews, setLoadingViews] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [viewName, setViewName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState('PRIVATE');
  const [projectId, setProjectId] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [pinView, setPinView] = useState(false);
  const [busy, setBusy] = useState(false);
  const [renameId, setRenameId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [defaultApplied, setDefaultApplied] = useState(false);


  const handleQuickFilterClick = (key) => {
    const pageScope = filter === 'open' ? 'open' : filter === 'closed' ? 'closed' : filter === 'critical' ? 'critical' : null;
    if (pageScope && key === pageScope) {
      onSavedViewChange?.(null);
      onQuickFilterChange?.('all');
      return;
    }
    if (pageScope && ['open', 'critical', 'closed'].includes(key) && key !== pageScope) {
      onNavigateRegisterFilter?.(key);
      return;
    }
    onSavedViewChange?.(null);
    onQuickFilterChange?.(key);
  };

  const loadViews = useCallback(async () => {
    setLoadingViews(true);
    try {
      const res = await correspondenceAPI.getSavedViews();
      setSavedViews(res.data?.data || []);
    } catch (_e) {
      setSavedViews([]);
    } finally {
      setLoadingViews(false);
    }
  }, []);

  useEffect(() => { loadViews(); }, [loadViews]);

  useEffect(() => {
    if (defaultApplied || !savedViews.length || savedViewId) return;
    const def = savedViews.find((v) => v.isDefault);
    if (def) {
      setDefaultApplied(true);
      onApplySavedView?.(def);
      onSavedViewChange?.(def.id);
    }
  }, [savedViews, defaultApplied, savedViewId, onApplySavedView, onSavedViewChange]);

  const allQuickFilterLabel = useMemo(() => {
    if (filter === 'open') return 'All open';
    if (filter === 'closed') return 'All closed';
    if (filter === 'critical') return 'All critical';
    return 'All';
  }, [filter]);

  const quickFilters = useMemo(() => {
    const items = [{ key: 'all', label: allQuickFilterLabel, Icon: IconFilter }];
    if (canManagePersonalFlags) {
      items.push(
        { key: 'important', label: 'My Important', Icon: IconStar },
        { key: 'pinned', label: 'Pinned', Icon: IconPin },
      );
    }
    items.push(
      { key: 'open', label: 'Open', Icon: IconFolder },
      { key: 'critical', label: 'Critical', Icon: IconAlert },
      { key: 'overdue', label: 'Overdue', Icon: IconClock },
      { key: 'closed', label: 'Closed', Icon: IconCheckCircle },
    );
    return items;
  }, [allQuickFilterLabel, canManagePersonalFlags]);

  const activeSavedView = useMemo(
    () => savedViews.find((v) => String(v.id) === String(savedViewId)),
    [savedViews, savedViewId],
  );

  const clearSavedView = () => {
    onSavedViewChange?.(null);
    onQuickFilterChange?.('all');
  };

  const handleSaveView = async () => {
    if (!viewName.trim()) {
      notifyError('View name is required.');
      return;
    }
    setBusy(true);
    try {
      const snapshot = getCurrentViewSnapshot();
      await correspondenceAPI.createSavedView({
        viewName: viewName.trim(),
        description: description || null,
        visibility,
        projectId: visibility === 'PROJECT' ? Number(projectId) : null,
        filters: snapshot.filters,
        columns: snapshot.columns,
        sortBy: snapshot.sortBy,
        searchText: snapshot.searchText,
        pageSize: snapshot.pageSize,
        isDefault,
        isPinned: pinView,
      });
      notifySuccess('View saved.');
      setShowSaveModal(false);
      setViewName('');
      setDescription('');
      await loadViews();
    } catch (e) {
      notifyError(e.response?.data?.message || 'Unable to save view.');
    } finally {
      setBusy(false);
    }
  };

  const handleSelectView = (id) => {
    const view = savedViews.find((v) => String(v.id) === String(id));
    if (!view) {
      onSavedViewChange?.(null);
      onQuickFilterChange?.('all');
      return;
    }
    onSavedViewChange?.(view.id);
    onApplySavedView?.(view);
  };

  const handleSetDefault = async (view) => {
    try {
      await correspondenceAPI.setSavedViewDefault(view.id);
      notifySuccess('Default view updated.');
      await loadViews();
    } catch (e) {
      notifyError(e.response?.data?.message || 'Unable to set default.');
    }
  };

  const handleDuplicate = async (view) => {
    try {
      await correspondenceAPI.duplicateSavedView(view.id);
      notifySuccess('View duplicated.');
      await loadViews();
    } catch (e) {
      notifyError(e.response?.data?.message || 'Unable to duplicate view.');
    }
  };

  const handleDelete = async (view) => {
    if (!window.confirm(`Delete view "${view.viewName}"?`)) return;
    try {
      await correspondenceAPI.deleteSavedView(view.id);
      if (String(savedViewId) === String(view.id)) clearSavedView();
      notifySuccess('View deleted.');
      await loadViews();
    } catch (e) {
      notifyError(e.response?.data?.message || 'Unable to delete view.');
    }
  };

  const handleRename = async () => {
    if (!renameId || !renameValue.trim()) return;
    const view = savedViews.find((v) => v.id === renameId);
    if (!view) return;
    try {
      await correspondenceAPI.updateSavedView(renameId, { ...view, viewName: renameValue.trim() });
      setRenameId(null);
      notifySuccess('View renamed.');
      await loadViews();
    } catch (e) {
      notifyError(e.response?.data?.message || 'Unable to rename view.');
    }
  };

  const handlePinSavedView = async (view) => {
    try {
      await correspondenceAPI.pinSavedView(view.id, { isPinned: !view.isPinned });
      await loadViews();
    } catch (e) {
      notifyError(e.response?.data?.message || 'Unable to pin view.');
    }
  };

  return (
    <div className="bv-panel">
      <div className="bv-panel__header">
        <div className="bv-panel__title">
          <IconLayout />
          Quick views
          <span className="bv-panel__hint">Personal star/pin is separate from official priority</span>
        </div>
        {activeSavedView && (
          <div className="bv-active-badge">
            <IconBookmark />
            {activeSavedView.viewName}
            <button type="button" onClick={clearSavedView} title="Clear saved view" aria-label="Clear saved view">×</button>
          </div>
        )}
      </div>

      <div className="bv-quick-filters">
        {quickFilters.map(({ key, label, Icon }) => (
          <button
            key={key}
            type="button"
            className={`bv-quick-filter bv-quick-filter--${key} ${quickFilter === key && !savedViewId ? 'active' : ''}`}
            onClick={() => handleQuickFilterClick(key)}
          >
            {key === 'important' ? <IconStar filled={quickFilter === key && !savedViewId} /> : <Icon />}
            {label}
          </button>
        ))}
      </div>

      {canManageSavedViews && (
      <div className="bv-toolbar-row bv-panel-divider">
        <div className="bv-select-wrap">
          <IconBookmark />
          <select
            className="bv-select"
            value={savedViewId || ''}
            onChange={(e) => handleSelectView(e.target.value)}
            disabled={loadingViews}
            aria-label="Saved correspondence views"
          >
            <option value="">{loadingViews ? 'Loading views…' : 'Choose a saved view…'}</option>
            {savedViews.map((v) => (
              <option key={v.id} value={v.id}>
                {v.isPinned ? '★ ' : ''}{v.isDefault ? '● ' : ''}{v.viewName} ({v.visibility})
              </option>
            ))}
          </select>
        </div>
        <div className="bv-toolbar-actions">
          <button type="button" className="bv-btn-outline" onClick={() => setShowSaveModal(true)}>
            <IconSave />
            Save current view
          </button>
          <button type="button" className="bv-btn-outline" onClick={() => setShowManageModal(true)}>
            <IconLayout />
            Manage views
          </button>
        </div>
      </div>
      )}

      {showSaveModal && (
        <div className="modal-overlay bv-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Save correspondence view</h2>
              <button type="button" className="btn-close" onClick={() => setShowSaveModal(false)} aria-label="Close">
                <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="bv-form-group"><label htmlFor="corr-view-name">View name *</label><input id="corr-view-name" value={viewName} onChange={(e) => setViewName(e.target.value)} placeholder="e.g. My open critical items" /></div>
              <div className="bv-form-group"><label htmlFor="corr-view-desc">Description</label><textarea id="corr-view-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="Optional note" /></div>
              <div className="bv-form-group"><label htmlFor="corr-view-vis">Who can use this view</label>
                <select id="corr-view-vis" value={visibility} onChange={(e) => setVisibility(e.target.value)}>
                  {VISIBILITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              {visibility === 'PROJECT' && (
                <div className="bv-form-group"><label htmlFor="corr-view-project">Project *</label>
                  <select id="corr-view-project" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
                    <option value="">Select project…</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              )}
              <label className="bv-checkbox-row"><input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} /> Load automatically when I open the register</label>
              <label className="bv-checkbox-row"><input type="checkbox" checked={pinView} onChange={(e) => setPinView(e.target.checked)} /> Pin to top of saved views list</label>
            </div>
            <div className="modal-footer bv-modal-footer">
              <button type="button" className="btn-secondary" onClick={() => setShowSaveModal(false)}>Cancel</button>
              <button type="button" className="btn-primary" disabled={busy} onClick={handleSaveView}>{busy ? 'Saving…' : 'Save view'}</button>
            </div>
          </div>
        </div>
      )}

      {showManageModal && (
        <div className="modal-overlay bv-modal bv-modal--wide">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Manage saved views</h2>
              <button type="button" className="btn-close" onClick={() => setShowManageModal(false)} aria-label="Close">
                <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
              </button>
            </div>
            <div className="modal-body">
              {savedViews.length === 0 ? (
                <div className="bv-empty-state"><IconBookmark /><p>No saved views yet. Set filters, then click <strong>Save current view</strong>.</p></div>
              ) : (
                <div className="bv-item-list">
                  {savedViews.map((v) => (
                    <div key={v.id} className={`bv-item-card ${String(savedViewId) === String(v.id) ? 'is-selected' : ''}`}>
                      <div className="bv-item-card__meta">
                        <div className="bv-item-card__name">
                          {v.viewName}
                          <span className={`bv-badge ${visibilityBadgeClass(v.visibility)}`}>{v.visibility}</span>
                          {v.isDefault && <span className="bv-badge bv-badge--default">Default</span>}
                          {v.isPinned && <span className="bv-badge bv-badge--pinned">Pinned</span>}
                        </div>
                        {v.description && <div className="bv-item-card__desc">{v.description}</div>}
                      </div>
                      <div className="bv-item-card__actions">
                        <button type="button" className="bv-item-action" onClick={() => { handleSelectView(String(v.id)); setShowManageModal(false); }}>Apply</button>
                        <button type="button" className="bv-item-action" onClick={() => { setRenameId(v.id); setRenameValue(v.viewName); }}>Rename</button>
                        <button type="button" className="bv-item-action" onClick={() => handleDuplicate(v)}>Duplicate</button>
                        <button type="button" className="bv-item-action" onClick={() => handleSetDefault(v)}>Set default</button>
                        <button type="button" className="bv-item-action" onClick={() => handlePinSavedView(v)}>{v.isPinned ? 'Unpin' : 'Pin'}</button>
                        <button type="button" className="bv-item-action bv-item-action--danger" onClick={() => handleDelete(v)}>Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer bv-modal-footer"><button type="button" className="btn-secondary" onClick={() => setShowManageModal(false)}>Close</button></div>
          </div>
        </div>
      )}

      {renameId && (
        <div className="modal-overlay bv-modal">
          <div className="modal-content">
            <div className="modal-header"><h2>Rename view</h2></div>
            <div className="modal-body"><div className="bv-form-group"><label htmlFor="corr-rename">View name</label><input id="corr-rename" value={renameValue} onChange={(e) => setRenameValue(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleRename()} /></div></div>
            <div className="modal-footer bv-modal-footer">
              <button type="button" className="btn-secondary" onClick={() => setRenameId(null)}>Cancel</button>
              <button type="button" className="btn-primary" onClick={handleRename}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CorrespondenceViewsToolbar;
